import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { ApiError } from './errors'
import { language } from '@/i18n'

/**
 * Axios set up for Sanctum cookie sessions from the admin origin (ADR-001,
 * SPEC-002 §7.1): credentials always sent, CSRF token read from the admin's
 * own XSRF-TOKEN-ADMIN cookie. Never store tokens in localStorage.
 */
export const http: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  withXSRFToken: true,
  xsrfCookieName: 'XSRF-TOKEN-ADMIN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
})

type Handlers = {
  /** Session ended or never existed: go to sign-in. */
  onUnauthenticated: () => void
  /** Signed in but MFA not yet passed: go to the code or set-up screen. */
  onMfaRequired: (state: 'required' | 'enrollment_required') => void
  /** A sensitive action needs the password and a code (423). Resolve true to retry. */
  onReauthenticationRequired: () => Promise<boolean>
}

let handlers: Handlers = {
  onUnauthenticated: () => {},
  onMfaRequired: () => {},
  onReauthenticationRequired: async () => false,
}

export function setApiHandlers(next: Handlers): void {
  handlers = next
}

let csrfReady: Promise<void> | null = null

/** Fetches the CSRF cookie once (and again after a 419). */
export function ensureCsrfCookie(force = false): Promise<void> {
  if (!csrfReady || force) {
    csrfReady = axios
      .get('/sanctum/csrf-cookie', { withCredentials: true })
      .then(() => undefined)
      .catch((error: unknown) => {
        csrfReady = null
        throw ApiError.from(error)
      })
  }
  return csrfReady
}

type RetryableConfig = InternalAxiosRequestConfig & { _csrfRetried?: boolean; _reauthRetried?: boolean }

const AUTH_PATHS = ['/admin/auth/login', '/admin/auth/forgot-password', '/admin/auth/set-password']

http.interceptors.request.use(async (config) => {
  // The API answers in the admin's language where it has Marathi (catalog texts, messages).
  config.headers.set('Accept-Language', language.value)
  const method = (config.method ?? 'get').toLowerCase()
  if (!['get', 'head', 'options'].includes(method)) await ensureCsrfCookie()
  return config
})

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const apiError = ApiError.from(error)
    const config = error.config as RetryableConfig | undefined

    // Expired CSRF token: refresh it and retry once.
    if (apiError.status === 419 && config && !config._csrfRetried) {
      config._csrfRetried = true
      await ensureCsrfCookie(true)
      return http.request(config)
    }

    // Sensitive action: ask for password + code, then retry once.
    if (apiError.code === 'REAUTHENTICATION_REQUIRED' && config && !config._reauthRetried) {
      config._reauthRetried = true
      if (await handlers.onReauthenticationRequired()) return http.request(config)
    }

    if (apiError.code === 'MFA_REQUIRED') {
      const state = apiError.details[0]?.mfa === 'enrollment_required' ? 'enrollment_required' : 'required'
      handlers.onMfaRequired(state)
    } else if (apiError.status === 401 && !AUTH_PATHS.some((p) => config?.url?.startsWith(p))) {
      handlers.onUnauthenticated()
    }

    return Promise.reject(apiError)
  },
)
